import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useEmployeeEngagementDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.store';
import { useEmployeeEngagementKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/useEmployeeEngagementKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { HoursWorkedChart } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/HoursWorkedChart';
import { PickupsTable } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/PickupsTable';
import { LastAppOpenedChart } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/LastAppOpenedChart';
import { ShiftAcceptanceLeaderboard } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/ShiftAcceptanceLeaderboard';
import { TopIndividualFulfillmentTable } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/TopIndividualFulfillmentTable';
import { TopIndividualCancellationTable } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/TopIndividualCancellationTable';
import { CancellationByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/CancellationByPositionChart';
import { CalloutAcceptanceRateTable } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/CalloutAcceptanceRateTable';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  DashboardErrorContainer,
  WidgetGrid,
  FullWidthSection,
} from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.styled';
import { useDocumentTitle } from 'hooks/useDocumentTitle';

export const EmployeeEngagementDashboard: React.FC = () => {
  const fetchData = useEmployeeEngagementDashboardStore((s) => s.fetchData);
  const reset = useEmployeeEngagementDashboardStore((s) => s.reset);
  const isLoading = useEmployeeEngagementDashboardStore((s) => s.isLoading);
  const error = useEmployeeEngagementDashboardStore((s) => s.error);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const kpiCards = useEmployeeEngagementKpiCards();
  const showSkeleton = isLoading;

  useDocumentTitle('Reports');

  useEffect(() => {
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label="Employee Engagement Dashboard">
      <DashboardHeader>
        <DashboardTitle>Employee Engagement</DashboardTitle>
      </DashboardHeader>
      <FilterBar />
      <DashboardContent>
        {error ? (
          <div aria-live="assertive">
            <DashboardErrorContainer>{error}</DashboardErrorContainer>
          </div>
        ) : showSkeleton ? (
          <DashboardSkeleton kpiCount={6} widgetCount={8} />
        ) : (
          <Fragment>
            <KpiStatCardRow cards={kpiCards.slice(0, 4)} />
            <KpiStatCardRow cards={kpiCards.slice(4)} />
            <WidgetGrid>
              <WidgetCardWrapper title="# of Hours worked" subtitle="Hours worked by team member">
                <HoursWorkedChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Total Pickups"
                subtitle="Team members who picked up the most shifts"
              >
                <PickupsTable />
              </WidgetCardWrapper>
            </WidgetGrid>
            <WidgetGrid>
              <WidgetCardWrapper
                title="Top individual fulfillment"
                subtitle="Fastest to respond to shift offers"
              >
                <TopIndividualFulfillmentTable />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Top individual cancellation"
                subtitle="Team members with highest post-accept cancellation rate"
              >
                <TopIndividualCancellationTable />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Cancellation by Position"
                subtitle="Post-accept cancellation rate by position"
              >
                <CancellationByPositionChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Call out acceptance rate & response speed"
                subtitle="Individual acceptance rates and average time to respond to call out offers"
              >
                <CalloutAcceptanceRateTable />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Last App Opened"
                subtitle="When did team members last open the mobile app?"
              >
                <LastAppOpenedChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Shift acceptance leaderboard by position"
                subtitle="Team members ranked by number of shifts accepted"
              >
                <ShiftAcceptanceLeaderboard />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
